import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Button, Table, Badge, Form, Modal, Spinner } from "react-bootstrap";
import { getDataAPI, postDataAPI } from "../../../services/apiWrapper";
import { ALERT } from "../../../store/actions/actionTypes";
import { downloadAuthedPdf } from "../../../utils/apiDownload";
import { exportListExcel, printListPdf } from "../../../utils/listExport";

/**
 * Council > Placement > Employers.
 * Approve / reject, edit, activate / deactivate employers, issue their
 * Partner ID, download their Authorization Certificate / ID card / registration
 * form, and export the list (Excel / PDF). The council approves EMPLOYERS; jobs
 * auto-go-live without approval.
 */
const token = () => localStorage.getItem("adminToken");

const statusBadge = (s) => {
  const n = Number(s);
  if (n === 1) return <Badge variant="success">Approved</Badge>;
  if (n === 2) return <Badge variant="danger">Rejected</Badge>;
  return <Badge variant="warning">Pending</Badge>;
};

const EMPTY_EDIT = {
  org_name: "", hr_name: "", hr_mobile: "", email: "",
  reg_number: "", support_number: "", state: "", city: "", address: "",
};

export default function Employers() {
  const dispatch = useDispatch();
  const alert = (payload) => dispatch({ type: ALERT, payload });

  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ current_page: 1, last_page: 1, total: 0 });
  const [status, setStatus] = useState("0");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [busy, setBusy] = useState(false);

  const [editRow, setEditRow] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_EDIT);
  const [saving, setSaving] = useState(false);

  const load = async (toPage = page) => {
    setBusy(true);
    try {
      const res = await dispatch(
        getDataAPI(
          `admin/placement/employers?status=${status}&search=${encodeURIComponent(search)}&page=${toPage}`,
          token()
        )
      );
      const d = res.data.data || {};
      setRows(d.data || []);
      setMeta({ current_page: d.current_page || 1, last_page: d.last_page || 1, total: d.total || 0 });
      setPage(d.current_page || 1);
    } catch (e) {
      alert({ error: "Could not load employers." });
    }
    setBusy(false);
  };

  useEffect(() => { load(1); /* eslint-disable-next-line */ }, [status]);

  const approve = async (id) => {
    if (!window.confirm("Approve this employer? A Partner ID is issued and they can log in and post jobs.")) return;
    setBusy(true);
    try {
      const res = await dispatch(postDataAPI(`admin/placement/employers/${id}/approve`, {}, token()));
      alert({ success: res.data.message || "Approved." });
      load();
    } catch (e) { alert({ error: "Could not approve." }); }
    setBusy(false);
  };

  const reject = async (id) => {
    const reason = window.prompt("Reason for rejection (shown to the employer):", "");
    if (reason === null) return;
    setBusy(true);
    try {
      const res = await dispatch(postDataAPI(`admin/placement/employers/${id}/reject`, { reason }, token()));
      alert({ success: res.data.message || "Rejected." });
      load();
    } catch (e) { alert({ error: "Could not reject." }); }
    setBusy(false);
  };

  const toggleActive = async (e) => {
    const next = e.is_active === 0 ? 1 : 0;
    if (!window.confirm(next ? "Activate this employer?" : "Deactivate this employer? They will not be able to log in.")) return;
    setBusy(true);
    try {
      const res = await dispatch(postDataAPI(`admin/placement/employers/${e.id}/active`, { active: next }, token()));
      alert({ success: res.data.message });
      load();
    } catch (err) { alert({ error: "Could not change status." }); }
    setBusy(false);
  };

  const openEdit = (e) => {
    setEditRow(e);
    setEditForm({
      org_name: e.org_name || "", hr_name: e.hr_name || "", hr_mobile: e.hr_mobile || "",
      email: e.email || "", reg_number: e.reg_number || "", support_number: e.support_number || "",
      state: e.state || "", city: e.city || "", address: e.address || "",
    });
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const res = await dispatch(postDataAPI(`admin/placement/employers/${editRow.id}/update`, editForm, token()));
      alert({ success: res.data.message || "Updated." });
      setEditRow(null);
      load();
    } catch (err) {
      const msg = err.response?.data?.data
        ? Object.values(err.response.data.data)[0][0]
        : "Could not update.";
      alert({ error: msg });
    }
    setSaving(false);
  };

  const doc = (id, kind, label) => {
    const map = {
      certificate: ["certificate", "certificate"],
      idcard: ["id-card", "id-card"],
      form: ["registration-pdf", "registration"],
    };
    const [path, name] = map[kind];
    downloadAuthedPdf(`admin/placement/employers/${id}/${path}`, token(), `employer-${name}-${id}.pdf`)
      .catch(() => alert({ error: `Could not download the ${label}.` }));
  };

  const columns = [
    { key: (r) => r.partner_id || "-", label: "Partner ID" },
    { key: "org_name", label: "Organization" },
    { key: "reg_number", label: "Reg No." },
    { key: "hr_name", label: "HR" },
    { key: "email", label: "Email" },
    { key: "hr_mobile", label: "Mobile" },
    { key: (r) => [r.city, r.state].filter(Boolean).join(", "), label: "City/State" },
    { key: (r) => (Number(r.status) === 1 ? "Approved" : Number(r.status) === 2 ? "Rejected" : "Pending"), label: "Status" },
    { key: (r) => (r.is_active === 0 ? "Inactive" : "Active"), label: "Active" },
  ];

  const fetchAllRows = async () => {
    const res = await dispatch(
      getDataAPI(`admin/placement/employers?status=${status}&search=${encodeURIComponent(search)}&per_page=100000`, token())
    );
    return (res.data.data && res.data.data.data) || [];
  };

  const exportExcel = async () => {
    try { exportListExcel(columns, await fetchAllRows(), "placement-employers"); }
    catch (e) { alert({ error: "Could not export." }); }
  };
  const exportPdf = async () => {
    try { printListPdf("Placement — Employers", columns, await fetchAllRows()); }
    catch (e) { alert({ error: "Could not export." }); }
  };

  return (
    <div className="container-fluid py-4">
      <h4 className="mb-3">Placement &mdash; Employers</h4>

      <div className="d-flex flex-wrap align-items-end mb-3" style={{ gap: 12 }}>
        <Form.Group className="mb-0">
          <Form.Label className="small mb-1">Status</Form.Label>
          <Form.Control as="select" value={status} onChange={(e) => setStatus(e.target.value)} style={{ width: 150 }}>
            <option value="0">Pending</option>
            <option value="1">Approved</option>
            <option value="2">Rejected</option>
            <option value="all">All</option>
          </Form.Control>
        </Form.Group>
        <Form.Group className="mb-0">
          <Form.Label className="small mb-1">Search</Form.Label>
          <Form.Control
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load(1)}
            placeholder="Org / email / HR / mobile"
            style={{ width: 230 }}
          />
        </Form.Group>
        <Button variant="primary" onClick={() => load(1)} disabled={busy}>Search</Button>
        <div className="ml-auto d-flex" style={{ gap: 8 }}>
          <Button variant="outline-success" size="sm" onClick={exportExcel}>Excel</Button>
          <Button variant="outline-secondary" size="sm" onClick={exportPdf}>PDF</Button>
        </div>
      </div>

      <Table bordered hover responsive size="sm">
        <thead className="thead-light">
          <tr>
            <th>Partner ID</th><th>Organization</th><th>HR</th><th>Email</th><th>Mobile</th>
            <th>City</th><th>Documents</th><th>Status</th><th>Action</th>
          </tr>
        </thead>
        <tbody>
          {busy && (
            <tr><td colSpan={9} className="text-center py-3"><Spinner animation="border" size="sm" /></td></tr>
          )}
          {!busy && rows.length === 0 && (
            <tr><td colSpan={9} className="text-center text-muted py-3">No employers.</td></tr>
          )}
          {!busy && rows.map((e) => (
            <tr key={e.id}>
              <td>{e.partner_id || <span className="text-muted">-</span>}</td>
              <td>{e.org_name}<br /><small className="text-muted">{e.reg_number}</small></td>
              <td>{e.hr_name}</td>
              <td>{e.email}</td>
              <td>{e.hr_mobile}</td>
              <td>{e.city}{e.state ? `, ${e.state}` : ""}</td>
              <td style={{ whiteSpace: "nowrap" }}>
                {Number(e.status) === 1 ? (
                  <>
                    <Button variant="link" size="sm" className="p-0 mr-2" onClick={() => doc(e.id, "certificate", "certificate")}>Certificate</Button>
                    <Button variant="link" size="sm" className="p-0 mr-2" onClick={() => doc(e.id, "idcard", "ID card")}>ID Card</Button>
                  </>
                ) : null}
                <Button variant="link" size="sm" className="p-0" onClick={() => doc(e.id, "form", "registration form")}>Reg Form</Button>
              </td>
              <td>{statusBadge(e.status)}{Number(e.status) === 1 && e.is_active === 0 ? <><br /><Badge variant="secondary">Inactive</Badge></> : null}</td>
              <td style={{ whiteSpace: "nowrap" }}>
                {Number(e.status) !== 1 && (
                  <Button size="sm" variant="success" className="mr-1 mb-1" disabled={busy} onClick={() => approve(e.id)}>Approve</Button>
                )}
                {Number(e.status) !== 2 && (
                  <Button size="sm" variant="outline-danger" className="mr-1 mb-1" disabled={busy} onClick={() => reject(e.id)}>Reject</Button>
                )}
                <Button size="sm" variant="outline-primary" className="mr-1 mb-1" onClick={() => openEdit(e)}>Edit</Button>
                {Number(e.status) === 1 && (
                  <Button size="sm" variant={e.is_active === 0 ? "outline-success" : "outline-secondary"} className="mb-1" onClick={() => toggleActive(e)}>
                    {e.is_active === 0 ? "Activate" : "Deactivate"}
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      <div className="d-flex align-items-center" style={{ gap: 10 }}>
        <small className="text-muted">Page {meta.current_page} of {meta.last_page} &bull; {meta.total} total</small>
        <div className="ml-auto">
          <Button size="sm" variant="light" disabled={busy || page <= 1} onClick={() => load(page - 1)}>Prev</Button>{" "}
          <Button size="sm" variant="light" disabled={busy || page >= meta.last_page} onClick={() => load(page + 1)}>Next</Button>
        </div>
      </div>

      <Modal show={!!editRow} onHide={() => setEditRow(null)} centered>
        <Modal.Header closeButton><Modal.Title>Edit Employer</Modal.Title></Modal.Header>
        <Modal.Body>
          {[
            ["org_name", "Organization *"], ["reg_number", "Registration No."],
            ["hr_name", "HR / Contact person"], ["hr_mobile", "Mobile"],
            ["email", "Email *"], ["support_number", "Support number"],
            ["city", "City"], ["state", "State"],
          ].map(([k, label]) => (
            <Form.Group key={k} className="mb-2">
              <Form.Label className="small mb-1">{label}</Form.Label>
              <Form.Control
                value={editForm[k]}
                onChange={(ev) => setEditForm((f) => ({ ...f, [k]: ev.target.value }))}
              />
            </Form.Group>
          ))}
          <Form.Group className="mb-0">
            <Form.Label className="small mb-1">Address</Form.Label>
            <Form.Control as="textarea" rows={2} value={editForm.address}
              onChange={(ev) => setEditForm((f) => ({ ...f, address: ev.target.value }))} />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={() => setEditRow(null)}>Cancel</Button>
          <Button variant="primary" onClick={saveEdit} disabled={saving}>{saving ? "Saving..." : "Save"}</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
